import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import images from "../../assets/images";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../styles/ag-grid-custom.css";
import { ColDef } from "ag-grid-community";
import { ModuleRegistry } from "ag-grid-community";
import { CellStyleModule, PaginationModule } from "ag-grid-community";
import { Search, ChevronDown, Download } from "lucide-react";
import ActionModal from "./components/ActionModal";
import CreateIncident from "./components/CreateIncidentModal";
import { usePremisesStore, useRegionStore } from "../../store/store";
import { generateIncidentExcel, formatDate } from "./utils/utils";
import { toast } from "react-toastify";
import { Dropdown } from "../../components/dropdown/Dropdown";
import { Incident } from "./types/types";
import {
  fetchIncidentActionsWithQuery,
  fetchRCAData,
} from "./services/service";
import { usePermissions } from "../../hooks/usePermission";
import { INCIDENT_LIST } from "../../constants/modules";
import { useIncidentsQuery, useInvalidateIncidents } from "./hooks/useIncidentQueries";

ModuleRegistry.registerModules([CellStyleModule, PaginationModule]);

const Incidents = () => {
  const { canCreate, canUpdate, canDelete, canRead } = usePermissions();
  const currentModule = INCIDENT_LIST;

  const [isOpen, setIsOpen] = useState(false);
  const [isRCA, setIsRCA] = useState(false);
  const [isDownload, setIsDownload] = useState(false);
  const [isEmergencyModal, setIsEmergencyModal] = useState(false);
  const [isIncidentModal, setIsIncidentModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<Incident | undefined>(
    undefined,
  );
  const [mode, setMode] = useState<"create" | "view" | "update">("create");
  const [actionId, setActionId] = useState<string | undefined>(undefined);
  const [rcaId, setRcaId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedPremises, setSelectedPremises] = useState<string>("");
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const gridRef = useRef<any>(null);

  const invalidateIncidents = useInvalidateIncidents();

  const { data: queryData, isLoading: loading } = useIncidentsQuery({
    currentPage,
    pageSize,
    debouncedSearchTerm,
    statusFilter,
  });

  const rowData = queryData?.incidents ?? [];
  const totalIncidents = queryData?.total ?? 0;

  const [dropdownAction, setDropdownAction] = useState<{
    rect: {
      top: number;
      left: number;
      width: number;
      height: number;
      bottom: number;
      right: number;
    };
    rowData: Incident;
  } | null>(null);

  const { fetchPremises } = usePremisesStore();
  const { fetchRegions } = useRegionStore();

  useEffect(() => {
    fetchPremises();
    fetchRegions();
  }, [fetchPremises, fetchRegions]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Data fetching is handled by useIncidentsQuery hook above.
  // Cross-component refresh is handled by queryClient.invalidateQueries.

  useEffect(() => {
    if (currentPage !== 0) {
      setCurrentPage(0);
    }
  }, [debouncedSearchTerm, statusFilter]);

  const handleActionComplete = () => {
    setIsOpen(false);
    setIsRCA(false);
    setIsDownload(false);
    setIsEmergencyModal(false);
    setIsIncidentModal(false);
    setSelectedRowData(undefined);
    setMode("create");
    setActionId(undefined);
    setRcaId(null);

    invalidateIncidents();
  };

  const handleViewIncident = (data: Incident) => {
    setSelectedRowData(data);
    setMode("view");

    if (data.status === "Open") {
      setIsIncidentModal(true);
      setIsOpen(false);
      setIsRCA(false);
    } else if (data.status === "Pending") {
      setActionId(data.hasActions && data.actionId ? data.actionId : undefined);
      setRcaId(data.hasRca && data.rcaId ? data.rcaId : null);
      setIsOpen(true);
      setIsRCA(false);
      setIsIncidentModal(false);
    }
  };

  const handleUpdateAction = (data: Incident) => {
    setSelectedRowData(data);
    setMode("update");

    if (data.status === "Open") {
      console.warn(
        "Update action called for Open incident - this should be disabled",
      );
      return;
    } else if (data.status === "Pending" && data.hasActions) {
      setActionId(data.actionId || undefined);
      setRcaId(data.hasRca && data.rcaId ? data.rcaId : null);
      setIsOpen(true);
      setIsRCA(false);
      setIsIncidentModal(false);
    }
  };

  const handleDownloadIncident = async (data: Incident) => {
    try {
      let sendModalData = null;
      let rcaData = null;

      if (
        data.status.toLowerCase() === "pending" ||
        data.status.toLowerCase() === "resolved"
      ) {
        try {
          const sendResponse = await fetchIncidentActionsWithQuery({
            incident: data.id,
          });
          sendModalData = sendResponse.data?.[0] || null;
        } catch (actionError) {
          console.warn("No action data found for incident:", data.id);
          // Continue execution even if action data is not found
        }
      }

      const shouldFetchRca =
        data.status.toLowerCase() === "resolved" ||
        ["in progress", "completed"].includes(
          String(data.rcaStatus || "").toLowerCase(),
        ) ||
        Boolean(data.hasRca || data.rcaId);

      if (shouldFetchRca) {
        try {
          const rcaResponse = await fetchRCAData({ incident: data.id });
          rcaData = rcaResponse.data?.[0] || null;
        } catch (rcaError) {
          console.warn("No RCA data found for incident:", data.id);
          // Continue execution even if RCA data is not found
        }
      }

      generateIncidentExcel(data, sendModalData as any, rcaData as any);
    } catch (error) {
      console.error("Error preparing Excel download:", error);
      toast.error("Failed to generate Excel file");
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
  };

  const filteredData = rowData.filter((incident: Incident) => {
    const matchesSearch = incident.incidentNumber
      ?.toLowerCase()
      .includes(debouncedSearchTerm.toLowerCase());
    const matchesPremises = selectedPremises
      ? incident.hospitalName === selectedPremises
      : true;
    const matchesRegion = selectedRegion
      ? incident.region === selectedRegion
      : true;
    return matchesSearch && matchesPremises && matchesRegion;
  });

  const onPaginationChanged = () => {
    if (gridRef.current && gridRef.current.api) {
      const newPage = gridRef.current.api.paginationGetCurrentPage();
      if (newPage !== currentPage) {
        setCurrentPage(newPage);
      }
    }
  };

  const columnDefs: ColDef[] = [
    {
      headerName: "Incident Number",
      field: "incidentNumber",
      flex: 1,
      minWidth: 150,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 5px " },
    },
    {
      headerName: "Report Date",
      field: "reportDate",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0 " },
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "Department",
      field: "department",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0 " },
    },
    {
      headerName: "Event Type",
      field: "moduleSpecificData.incidentEventType",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0 " },
    },
    {
      headerName: "Incident Date",
      field: "incidentDate",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0 " },
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "Days Open",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
      valueGetter: (params) => {
        if (!params.data) return "—";
        const startDateStr = params.data.reportDate || params.data.incidentDate;
        if (!startDateStr) return "—";

        const start = new Date(startDateStr);
        start.setHours(0, 0, 0, 0);

        const end = params.data.status === "Resolved" && params.data.closureDate
          ? new Date(params.data.closureDate)
          : new Date();
        end.setHours(0, 0, 0, 0);

        const diffTime = end.getTime() - start.getTime();
        const diffDays = Math.max(0, Math.round(diffTime / (1000 * 60 * 60 * 24)));
        return `${diffDays} Days`;
      },
    },
    {
      headerName: "Reported By",
      field: "reportedBy",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0 " },
    },
    {
      headerName: "Corrective Action",
      field: "implementedCorrectiveActions",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: { padding: "10px 0 0 0" },
      valueFormatter: (params) => params.value ? "Completed" : "Pending",
    },
    {
      headerName: "Incident Status",
      field: "status",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: (params) => {
        let color = "black";
        let borderStyle = "none";

        switch (params.value) {
          case "Open":
            color = "#eb665c";
            borderStyle = "1px solid #eb665c";
            break;
          case "Pending":
            color = "#fa9c4c";
            borderStyle = "1px solid #fa9c4c";
            break;
          default:
            color = "#666666";
            borderStyle = "1px solid #666666";
            break;
        }

        return {
          color: color,
          fontWeight: "semibold",
          padding: "10px 0 0 0",
          border: borderStyle,
          borderRadius: "4px",
        };
      },
    },
    {
      headerName: "RCA Status",
      field: "rcaStatus",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellStyle: (params) => {
        let color = "#999";
        if (params.value === "Completed") color = "#4CAF50";
        else if (params.value === "In Progress") color = "#FF9800";
        else if (params.value === "Draft") color = "#2196F3";
        return { color, fontWeight: "600", padding: "10px 0 0 0" };
      },
      valueFormatter: (params) => {
        if (!params.data) return "—";
        if (params.data.status === "Open") return "N/A";
        if (params.value === "Completed") return "Completed";
        if (params.value === "In Progress") return "In Progress";
        if (params.value === "Draft") return "Draft";
        return "Not Started";
      },
    },
    {
      headerName: "Action",
      field: "status",
      minWidth: 160,
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      cellStyle: { padding: "10px 0" },
      cellRenderer: (params: any) => {
        const hasUpdatePermission = canUpdate(currentModule);
        const hasCreatePermission = canCreate(currentModule);
        const hasActionPermission = hasUpdatePermission || hasCreatePermission;

        const isUpdateDisabled =
          !hasUpdatePermission ||
          params.data.status === "Open" ||
          (params.data.status === "Pending" && !params.data.hasActions);

        const data = params.data;

        return (
          <div className="flex items-center gap-2 justify-center h-full">
            {/* Primary Workflow Actions (Send/Emergency OR RCA) */}
            {data.status === "Open" && hasActionPermission && (
              <div className="flex items-center shadow-sm rounded-md">
                {!data.hasEmergency && (
                  <button
                    type="button"
                    className="bg-[#eb665c] hover:bg-[#d6554b] text-white rounded-l-md px-3 py-1 font-semibold text-xs transition-colors duration-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedRowData(data);
                      setMode("create");
                      setActionId(undefined);
                      setRcaId(null);
                      setIsOpen(true);
                      setIsRCA(false);
                      setIsEmergencyModal(false);
                      setIsIncidentModal(false);
                      setDropdownAction(null);
                    }}
                  >
                    SEND
                  </button>
                )}
                <button
                  type="button"
                  className={`bg-[#d6554b] hover:bg-[#c24c43] text-white px-1 py-1 transition-colors duration-200 ${!data.hasEmergency
                      ? "rounded-r-md border-l border-[#eb665c]"
                      : "rounded-md px-2"
                    }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (dropdownAction?.rowData.id === data.id) {
                      setDropdownAction(null);
                    } else {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setDropdownAction({
                        rect: {
                          top: rect.top,
                          bottom: rect.bottom,
                          left: rect.left,
                          right: rect.right,
                          width: rect.width,
                          height: rect.height,
                        },
                        rowData: data,
                      });
                    }
                  }}
                >
                  <ChevronDown size={14} />
                </button>
              </div>
            )}

            {data.status === "Pending" && hasActionPermission && (
              <button
                type="button"
                className="bg-[#fa9c4c] hover:bg-[#e78c3b] text-white rounded-md shadow-sm px-4 py-1 font-semibold text-xs transition-colors duration-200"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRowData(data);
                  setMode("create");
                  setActionId(data.actionId ?? undefined);
                  setRcaId(data.rcaId || null);
                  setIsRCA(true);
                  setIsOpen(false);
                  setIsEmergencyModal(false);
                  setIsIncidentModal(false);
                  setDropdownAction(null);
                }}
              >
                RCA
              </button>
            )}

            {/* Standard Actions (View / Edit / Download) */}
            <div className="flex items-center gap-1.5 border-l pl-2 ml-1 border-gray-300">
              <button
                type="button"
                className="p-1 hover:scale-110 transition-transform duration-200"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewIncident(data);
                }}
                title="View"
              >
                <img className="w-4 h-4" src={images.viewIcon} alt="View" />
              </button>
              <button
                type="button"
                disabled={isUpdateDisabled}
                className={`p-1 transition-transform duration-200 ${isUpdateDisabled
                    ? "opacity-40 cursor-not-allowed"
                    : "hover:scale-110 cursor-pointer"
                  }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleUpdateAction(data);
                }}
                title={
                  isUpdateDisabled ? "No Permission or Invalid Status" : "Edit"
                }
              >
                <img className="w-4 h-4" src={images.editIcon} alt="Edit" />
              </button>
              <button
                type="button"
                className="p-1 hover:scale-110 transition-transform duration-200"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownloadIncident(data);
                }}
                title="Download"
              >
                <img
                  className="w-4 h-4"
                  src={images.downloadIcon}
                  alt="Download"
                />
              </button>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="mt-16 px-2 py-4 space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-semibold">Incidents</h1>
          <div className="flex items-center gap-4">
            {/* {canRead(currentModule) && (
              <button
                className="flex items-center justify-center font-semibold gap-3 bg-[#4f5762] text-white px-4 py-2 rounded-full"
                onClick={() => {
                  setSelectedRowData(undefined);
                  setIsDownload(true);
                }}
              >
                <img src={images.downloadIcon} alt="" className="w-5 h-5 text-white" />
                BULK EXPORT
              </button>
            )} */}
            <Dropdown
              selectedValue={selectedRegion}
              onChange={setSelectedRegion}
              type="region"
            />
            <Dropdown
              selectedValue={selectedPremises}
              onChange={setSelectedPremises}
              type="premises"
              selectedRegion={selectedRegion}
            />
          </div>
        </div>

        <div className="bg-white w-full h-full drop-shadow-[0_0px_10px_rgba(0,0,0,0.25)]">
          <div className="p-6">
            <div className="flex justify-between ">
              <div className="flex gap-5 items-center">
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search by Incident Number"
                    className="border-2 border-[#4f5762] w-[300px] px-10 py-2 rounded placeholder:font-semibold placeholder:text-[#4e545d]"
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                </div>

                <select
                  name="status"
                  id="status"
                  className="border-2 border-[#4f5762] w-[250px] px-3 py-2 rounded font-semibold text-[#4e545d]"
                  value={statusFilter}
                  onChange={handleStatusChange}
                >
                  <option value="">Select Status</option>
                  <option value="Open">Open</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedRowData(undefined);
                  setIsOpen(false);
                  setIsRCA(false);
                  setIsEmergencyModal(false);
                  setIsIncidentModal(false);
                  setIsDownload(true);
                }}
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded font-semibold hover:bg-teal-700"
              >
                <Download size={18} />
                Bulk Download
              </button>
            </div>
          </div>

          <div className="ag-theme-alpine">
            {loading ? (
              <div className="p-4 text-center">Loading incidents...</div>
            ) : (
              <AgGridReact
                ref={gridRef}
                rowData={filteredData}
                columnDefs={columnDefs}
                rowHeight={50}
                headerHeight={65}
                paginationPageSize={pageSize}
                pagination={true}
                domLayout="autoHeight"
                onPaginationChanged={onPaginationChanged}
                defaultColDef={{
                  sortable: true,
                  resizable: true,
                }}
                getRowStyle={(params) => {
                  let backgroundColor = "white";
                  // Highlight in red if RCA is Completed but Incident is Pending (means Corrective Actions are not fully completed)
                  if (
                    params.data &&
                    params.data.rcaStatus === "Completed" &&
                    params.data.status === "Pending"
                  ) {
                    backgroundColor = "#fee2e2"; // light red background for visibility
                  }
                  return {
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    marginBottom: "8px",
                    borderRadius: "4px",
                    backgroundColor,
                  };
                }}
              />
            )}
          </div>
        </div>
      </div>

      {(isOpen || isRCA || isDownload || isEmergencyModal) && (
        <ActionModal
          showModal={isOpen}
          showRCAModal={isRCA}
          showDownload={isDownload}
          showEmergencyModal={isEmergencyModal}
          setShowRCAModal={setIsRCA}
          setShowModal={setIsOpen}
          setShowDownload={setIsDownload}
          setShowEmergencyModal={setIsEmergencyModal}
          data={selectedRowData}
          onActionComplete={handleActionComplete}
          setSelectedRowData={setSelectedRowData}
          mode={mode}
          actionId={actionId}
          setRcaId={setRcaId}
          rcaId={rcaId || null}
          moduleKey={currentModule}
        />
      )}

      {isIncidentModal && (
        <CreateIncident
          showModal={isIncidentModal}
          setShowModal={setIsIncidentModal}
          departmentName={selectedRowData?.department}
          onIncidentCreated={handleActionComplete}
          mode={mode}
          incidentId={selectedRowData?.id}
          moduleKey={currentModule}
        />
      )}

      {/* Action Dropdown Portal */}
      {dropdownAction &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[9998]"
              onClick={(e) => {
                e.stopPropagation();
                setDropdownAction(null);
              }}
            />
            <div
              className="fixed z-[9999] bg-white rounded-md shadow-lg border border-gray-200 py-1 min-w-[130px]"
              style={{
                top: dropdownAction.rect.bottom + 4,
                right: window.innerWidth - dropdownAction.rect.right,
              }}
            >
              {!dropdownAction.rowData.hasEmergency ? (
                <button
                  type="button"
                  className="w-full text-left px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedRowData(dropdownAction.rowData);
                    setMode("create");
                    setActionId(undefined);
                    setRcaId(null);
                    setIsEmergencyModal(true);
                    setIsOpen(false);
                    setIsRCA(false);
                    setIsIncidentModal(false);
                    setDropdownAction(null);
                  }}
                >
                  EMERGENCY
                </button>
              ) : (
                <div className="px-4 py-2 text-xs text-gray-500 italic">
                  Already Emergency
                </div>
              )}
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

export default Incidents;
